import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { JurisdictionService } from "@services";
import classNames from "classnames";
import { Link } from "react-router";
import ProgressButton from "react-progress-button";
import StateSelection from "@components/common/StateSelection";
import { ROUTES } from "@constants/index";
import { ReactSelect, Image } from "../../../../components";
import TextLogo from "../../../../images/logo-text.svg";
import IconLogo from "../../../../images/logo-icon.svg";
import "./SignupLea.scss";

const SignupLea = ({ router }) => {
	const { lang } = global;
	const departmentStateSelection = React.createRef();
	const [buttonState, setButtonState] = useState("");
	const [counties, setCounties] = useState([]);
	const [jurisdictions, setJurisdictions] = useState([]);
	const [loadingCounties, setLoadingCounties] = useState(false);
	const [loadingJurisdictions, setLoadingJurisdictions] = useState(false);
	const [departmentInfo, setDepartmentInfo] = useState({
		state: "",
		countyId: "",
		countyName: "",
		jurisdictionId: "",
		jurisdictionName: "",
		errors: {
			state: false,
			countyId: false,
			jurisdictionId: false,
		},
	});

	/**
	 * Retrieves a list of counties per state
	 * @param {Number} stateId The unique id of a selected state
	 */
	const getCounties = stateId => {
		setLoadingCounties(true);

		JurisdictionService.getAll({ stateId })
			.then(({ data }) => {
				const countiesOptions = data.map((item, index) => ({ value: index, label: item }));
				setCounties(countiesOptions);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Error loading counties",
					timeOut: 1500,
				});
			})
			.finally(() => {
				setLoadingCounties(false);
			});
	};

	/**
	 * Retrieves a list of jurisdictions associated with a state
	 * @param {Number} stateId The unique id of a selected state
	 * @param {String} countyName The name of the county associated with a state
	 */
	const getJurisdictions = (stateId, countyName) => {
		setLoadingJurisdictions(true);
		JurisdictionService.getAll({ stateId, county: countyName })
			.then(({ data }) => {
				const jurisdictionsOptions = data.map(({ Id: value, Name: label }) => ({
					value,
					label,
				}));
				setJurisdictions(jurisdictionsOptions);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Error loading jurisdictions",
					timeOut: 1500,
				});
			})
			.finally(() => {
				setLoadingJurisdictions(false);
			});
	};

	/**
	 * Verify if a jurisdictionId is already taken for an organization
	 * @param {Number} jurisdictionId The unique id of a selected jurisdiction
	 */
	const verifyJurisdiction = jurisdictionId =>
		JurisdictionService.verifyJurisdictionIsAvailable(jurisdictionId);

	const handleOnStateChange = e => {
		const { value } = e.target;

		setCounties([]);
		setJurisdictions([]);
		setDepartmentInfo({
			...departmentInfo,
			jurisdictionId: "",
			jurisdictionName: "",
			countyId: "",
			countyName: "",
			state: value,
			errors: {
				...departmentInfo.errors,
				state: false,
			},
		});
		getCounties(value);
	};

	const handleOnSelectChange = name => e => {
		let value;
		let label;
		if (!e) {
			value = "";
			label = "";
		} else ({ value, label } = e);

		if (name === "county") {
			setDepartmentInfo({
				...departmentInfo,
				jurisdictions: [],
				countyId: value,
				countyName: label,
				jurisdictionId: "",
				jurisdictionName: "",
				errors: {
					...departmentInfo.errors,
					countyId: false,
				},
			});
			if (value !== "") {
				getJurisdictions(departmentInfo.state, departmentInfo.countyName);
			}
		}

		if (name === "jurisdictionId") {
			setDepartmentInfo({
				...departmentInfo,
				jurisdictionId: value,
				jurisdictionName: label,
				errors: {
					...departmentInfo.errors,
					jurisdictionId: false,
				},
			});
		}
	};

	/**
	 * Validates the required input values per section
	 * @param {String} activeForm The sectoin being validated
	 * @returns {Object} Determines the idf the section is valid
	 */
	const validate = () => {
		const {
			errors: { ...departmentInfoErrors },
		} = departmentInfo;

		let isValid = true;

		departmentInfoErrors.state = departmentInfo.state === "";
		departmentInfoErrors.countyId = departmentInfo.countyId === "";
		departmentInfoErrors.jurisdictionId = departmentInfo.jurisdictionId === "";

		setDepartmentInfo({
			...departmentInfo,
			errors: departmentInfoErrors,
		});

		// filter out all keys value pairs with existing errors. returns false if errors exist
		isValid = Object.values(departmentInfoErrors).filter(error => error === true).length === 0;

		return isValid;
	};

	const submit = () => {
		const { jurisdictionId, jurisdictionName } = departmentInfo;
		setButtonState("loading");
		verifyJurisdiction(jurisdictionId)
			.then(() => {
				router.push({
					pathname: ROUTES.signupLeaAD,
					search: `?${new URLSearchParams({ jurisdictionId, jurisdictionName }).toString()}`,
				});
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message
						? `${data.Message}`
						: lang.t("An error occurred verifying the selected jurisdiction"),
					timeOut: 1500,
				});
			})
			.finally(() => setButtonState(""));
	};

	/** Handles and validates all changes made to the department information section */
	const handleDepartmentInfo = () => {
		const validation = validate();
		if (validation) {
			submit();
		} else {
			setButtonState("error");
			setTimeout(() => {
				clearTimeout();
				setButtonState("");
			}, 1200);
		}
	};

	useEffect(() => {
		if (departmentInfo.state) {
			getCounties(departmentInfo.state);
		}
	}, [departmentInfo.state]);

	useEffect(() => {
		if (departmentInfo.state && departmentInfo.countyName) {
			getJurisdictions(departmentInfo.state, departmentInfo.countyName);
		}
	}, [departmentInfo.countyId]);

	return (
		<div id="ad-signup-lea">
			<nav className="navbar navbar-default navbar-static-top">
				<div className="container-fluid">
					<div className="row">
						<div className="col-xs-4 navbar-header">
							<div className="navbar-brand">
								<img
									src={IconLogo}
									className="visible-xs brand-img"
									alt="Brand"
								/>
								<img
									src={TextLogo}
									className="hidden-xs brand-img"
									alt="Brand"
								/>
								<span className="persona">{lang.t("LEA")}</span>
							</div>
						</div>
						<div className="col-xs-8 sign-in">
							<span>{lang.t("Already a member_")}</span>
							<Link
								className="btn btn-default"
								to="/login"
							>
								{lang.t("Sign In")}
							</Link>
						</div>
					</div>
				</div>
			</nav>
			<div className="container-fluid signup-content">
				<div className="row">
					<div className="signup-form col-md-offset-2 col-sm-6 col-md-5 col-sm-offset-1">
						<div className="signup-form-header row">
							<h2>
								<span className="lea-text">{lang.t("LEA")}</span>
								&nbsp;
								<span className="heading-text">{lang.t("Sign Up")}</span>
							</h2>
							<h4 className="text-muted">
								{lang.t("We will get you setup in a few steps")}
							</h4>
						</div>
						<div className="row signup-form-body">
							<strong className="col-xs-12 signup-form-text">
								{lang.t("Department Info")}
							</strong>

							<div
								className={classNames("col-xs-12 col-sm-6 form-group", {
									"has-error": departmentInfo.errors.state,
								})}
							>
								<label htmlFor="state">{lang.t("State")}</label>
								<StateSelection
									ref={departmentStateSelection}
									name="state"
									id="state"
									value={departmentInfo.state}
									onChange={handleOnStateChange}
								/>
							</div>

							<div
								className={classNames("col-xs-12 col-sm-6 form-group", {
									"has-error": departmentInfo.errors.countyId,
								})}
							>
								<label htmlFor="county">{lang.t("County")}</label>
								<ReactSelect
									name="county"
									id="county"
									value={departmentInfo.countyId}
									placeholder={lang.t("Select")}
									options={counties}
									onChange={handleOnSelectChange("county")}
									isLoading={loadingCounties}
									isClearable
								/>
							</div>
							<div
								className={classNames("col-xs-12 form-group", {
									"has-error": departmentInfo.errors.jurisdictionId,
								})}
							>
								<label htmlFor="jurisdictionId">{lang.t("Department")}</label>
								<ReactSelect
									name="jurisdictionId"
									id="jurisdictionId"
									placeholder={lang.t("Select")}
									value={departmentInfo.jurisdictionId}
									options={jurisdictions}
									onChange={handleOnSelectChange("jurisdictionId")}
									isLoading={loadingJurisdictions}
									isClearable
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-xs-12 signup-form-footer">
								<div className="col-xs-12 text-right">
									<ProgressButton
										id="button-departmentInfo"
										className="btn btn-primary continue-button"
										state={buttonState}
										onClick={handleDepartmentInfo}
									>
										{lang.t("Continue")}
										&nbsp;
										<i className="rkf rkf-arrow-right" />
									</ProgressButton>
								</div>
							</div>
						</div>
					</div>

					<div
						className={classNames(
							"signup-rollkall-promo text-center col-sm-5 hidden-xs",
						)}
					>
						<div className="text-center">
							<div className="promo-image">
								<Image
									className="img-responsive"
									src="/images/rollkall_promo_1.png"
									alt="promo image"
								/>
							</div>
							<div className="promo-text text-left">
								<h2>{lang.t("RollKall For LEA")}</h2>
								<p>{lang.t("Great benefits await by using RollKall, including")}</p>
								<ol>
									<li>{lang.t("Manage all of your officers")}</li>
									<li>{lang.t("Approve or decline requests")}</li>
									<li>{lang.t("Approve payments easily")}</li>
								</ol>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

SignupLea.propTypes = {
	router: PropTypes.object.isRequired,
};

export default SignupLea;
